import React, { Component } from 'react';
import { Button, Spinner, ListGroup, ListGroupItem, Alert } from 'reactstrap';

import { connect } from 'react-redux';
import Dropzone, { FileRejection } from 'react-dropzone';
import { raiseException, IDispatchRaiseException } from '../redux/notifications/actions';

interface IFileUploaderProps {
    maxFiles?: number;
    files: IFile[];
    onFileUpload: (file: IFile) => Promise<IFile>;
    onFileDelete: (file: IFile) => Promise<void>;
}
interface IReduxProps extends IFileUploaderProps {
}
interface IFileUploaderState {
    files: IFileDisplay[];
    uploading: boolean;
    errors: string[];
}
type IReduxDispatch = IDispatchRaiseException;

export interface IFile {
    name: string;
    size: number;
    // id: string;
    // url: string;

    error?: string;
    file?: File;
}
interface IFileDisplay extends IFile {
    formattedSize: string;
    status: 'queued' | 'uploading' | 'existing' | 'error';
}

const errorCodeMap = {
    'file-too-large': 'Het bestand is te groot. Bestanden mogen maximaal 500Mb zijn.',
}
class FileUploader extends Component<IReduxProps & IReduxDispatch, IFileUploaderState> {
    constructor(props) {
        super(props);

        this.state = {
            errors: null,
            files: [],
            uploading: false,
        };
    }
    private mountState = false;

    public componentDidMount() {
        const files: IFileDisplay[] = this.props.files
            .map((x) => ({ ...x, formattedSize: this.formatBytes(x.size), status: 'existing' }));

        this.setState({ files });
        this.mountState = true;
    }

    public componentDidUpdate(prev: IReduxProps) {
        if (this.props.files !== prev.files) {

            const busy: IFileDisplay[] = this.state.files
                .filter((x) => x.status !== 'existing')
                .sort((a, b) => a.name.localeCompare(b.name));

            const existing: IFileDisplay[] = this.props.files
                .filter((x) => !busy.some(e => x.name === e.name))
                .map((x) => ({ ...x, formattedSize: this.formatBytes(x.size), status: 'existing' }));

            const files = existing.concat(busy);

            this.setState({
                files,
            });
        }
    }

    public componentWillUnmount() {
        this.mountState = true;
    }
    /**
     * Handled the accepted files and shows the preview
     */
    private handleFiles(uploaded: File[], fileRejections: FileRejection[]) {
        if (fileRejections && fileRejections.length > 0) {
            this.setState({ errors: fileRejections.map((x) => x.errors.map(e => `${x.file.name}: ${(errorCodeMap[e.code] ? errorCodeMap[e.code] : `${e.message} (${e.code})`)}`)).reduce((p: string[], i: string[]) => p.concat(i), []) });
        }
        else {
            this.setState({ errors: null });
        }
        if (uploaded && uploaded.length > 0) {
            const added = uploaded.map<IFileDisplay>((file, index) => ({
                file,
                formattedSize: this.formatBytes(file.size),
                //id: `${Date.now()}-${index}`,
                name: file.name || `Uploaded_file_${Date.now().valueOf()}_${index}`,
                size: file.size,
                url: null,
                status: 'queued'
            }));

            let files = this.state.files
                .filter((x) => !added.some((a) => a.name === x.name))
                .concat(added);

            if (this.props.maxFiles > 0 && files.length > this.props.maxFiles)
                files = files.slice(0, this.props.maxFiles);

            this.setState({ files }, () => this.uploadQueued());
        }
    }

    private async uploadQueued() {
        const queued = this.state.files
            .filter(x => x.status === 'queued');

        if (queued.length === 0) {
            if (this.state.uploading)
                this.setState({ uploading: false });

            return;
        }
        if (!this.mountState)
            return;

        const file = queued[0];
        try {
            this.setState({ uploading: true, files: this.state.files.filter((x) => x.name !== file.name).concat([{ ...file, status: 'uploading' }]) });

            const uploaded = await this.props.onFileUpload(file);
            if (!this.mountState)
                return;
            this.setState({ files: this.state.files.filter((x) => x.name !== file.name && x.name !== uploaded.name).concat([{ ...uploaded, formattedSize: this.formatBytes(uploaded.size), status: 'existing' }]) });
        }
        catch (e) {
            this.props.raiseException(e, 'Error uploading file', 'warning');

            this.setState({ files: this.state.files.filter((x) => x.name !== file.name).concat([{ ...file, status: 'error', error: e.message }]) });
        }

        this.uploadQueued();
    }

    /**
     * Formats the size
     */
    private formatBytes(bytes: number, decimals: number = 2) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }
    private async removeFile(index: number) {
        this.setState({
            files: this.state.files.filter((_, i) => i !== index),
        });

        await this.props.onFileDelete(this.state.files[index]);
    }

    public render() {
        return <>
            {(this.props.maxFiles !== 1 || this.state.files.length !== 1) &&
                <div>
                    <Dropzone
                        onDrop={(acceptedFiles, fileRejections) => this.handleFiles(acceptedFiles, fileRejections)}
                        maxSize={524288000}
                        accept=".doc, .docx, .pdf, .txt, .xls, .xlsx, .xml, .wpd, .wps, .rtf, .csv, .txt, .sdw, .sgl, .vor, .uot, .uof, .jtd, .jtt, .hwp, .wk1, .wks, .sdc, .uos, .ppt, .pptx, .pps, .sxw, .xsc, .jpg, .png, .mov, .mp4, .wmv, .mkv, .avi, .mpg, .mpeg"
                        multiple={this.props.maxFiles > 1}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <div className="dropzone">
                                <div className="dz-message needsclick" {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <i className="h1 text-muted dripicons-cloud-upload"></i>
                                    <h3>Zet bestand{this.props.maxFiles === 1 ? '' : 'en'} hier neer of klik om te uploaden. </h3>
                                    {this.props.maxFiles > 0 && <div>(Maximaal {this.props.maxFiles === 1 ? '1 bestand' : `${this.props.maxFiles} bestanden`})</div>}
                                    <span className="text-muted font-13">

                                    </span>
                                </div>
                            </div>
                        )}
                    </Dropzone></div>}

            {this.state.errors !== null && this.state.errors.length > 0 &&
                <Alert color="warning">
                    Bestand niet geaccepteerd
                    <ul>
                        {this.state.errors.map((item, index) => <li key={index}>{item}</li>)}
                    </ul>
                </Alert>}
            {this.state.files.length > 0 && <ListGroup>
                {this.state.files.map((file, index) =>
                    <ListGroupItem key={file.name}>

                        {(file.status === 'existing' || file.status === 'error') &&
                            <Button color="danger" size="sm" onClick={() => this.removeFile(index)} >
                                <i className="mdi mdi-trash-can-outline" aria-label="Verwijderen" />
                            </Button>}
                        {file.status === 'queued' && <i className="mdi mdi-timer-sand-empty" aria-label="Wachten" />}
                        {file.status === 'uploading' && <Spinner size="sm" aria-label="Bezig" />}
                        {file.status === 'error' && <i className="mdi mdi-alert" aria-label={`Fout: ${file.error}`} title={file.error} />}
                        &nbsp;
                        {file.name} ({file.formattedSize})
                    </ListGroupItem>
                )}
            </ListGroup>
            }
        </>;
    }
}

export default connect(
    null,
    { raiseException },
)(FileUploader);
